import React from 'react';
import { makeStyles } from '@mui/styles';
import { Grid, Typography, InputBase } from '@quillbot/design-system';
import { useRouter } from 'next/router';
import { getCgBreakpointDown, getCgBreakpointUp } from 'src/citation-generator/utils';
import { FormValueKeys } from '../types';
import { useFieldConfig, useFieldValidator, useMultipleFieldConfig } from '../config';
import { useEditFormDispatch, useEditFormState } from '../context';

interface TextFiekdGroupWithLabelStyleProps {
  noOfTextFields: number;
  isCoWriter?: boolean;
}

const useTextFiekdGroupWithLabelStyle = makeStyles((theme) => ({
  inputsWrapper: {
    justifyContent: 'space-between',
    [getCgBreakpointDown(theme)]: {
      flexDirection: 'column',
    },
  },
  inputContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 3,
    width: '100%',
    height: '30px',
  },
  inputContainerOuterContainer: {
    borderRadius: 3,
    width: ({ noOfTextFields }: TextFiekdGroupWithLabelStyleProps) => {
      const width = `${100 / noOfTextFields}% - ${(8 / noOfTextFields) * (noOfTextFields - 1)}px`;
      return `calc(${width})`;
    },
    height: '46px',
    [getCgBreakpointDown(theme)]: {
      width: '100% !important',
    },
  },
  input: {
    padding: '0px 10px',
    fontSize: '13px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    width: '100%',
    [getCgBreakpointUp(theme)]: {
      '& > input::placeholder': {
        opacity: '0',
      },
    },
  },
  label: {
    float: 'right',
    fontSize: '12px',
    color: theme.palette.text.primary,
  },
  labelContainer: {
    width: '24%',
    paddingRight: (props: TextFiekdGroupWithLabelStyleProps) => (props.isCoWriter ? '10px' : '8px'),
    alignSelf: 'start',
    marginTop: 22,
    [getCgBreakpointDown(theme)]: {
      marginTop: 5,
    },
  },
  inputLabel: {
    color: theme.palette.text.secondary,
    fontSize: '12px',
    marginLeft: '5px',
    [getCgBreakpointDown(theme)]: {
      display: 'none',
    },
  },
  warning: {
    border: `1px solid ${theme.palette.secondary.main}`,
  },
}));

export const InTextModalField = (props: {
  value: { [key: string]: string | number };
  fields: any;
  onChange: Function;
  label: string;
}) => {
  const { onChange, label, value, fields } = props;
  const router = useRouter();
  const isCoWriter = router.pathname.includes('/co-writer');
  const classes = useTextFiekdGroupWithLabelStyle({ noOfTextFields: fields.length, isCoWriter });

  return (
    <Grid container>
      <Grid item className={classes.inputContainerOuterContainer}>
        <Typography className={classes.inputLabel}>{label}</Typography>

        <Grid className={`${classes.inputContainer}`}>
          <InputBase
            className={classes.input}
            placeholder={label}
            type={fields[0].type}
            value={value[fields[0].key]}
            onChange={(e) => {
              onChange({ ...value, [fields[0].key]: e.target.value });
            }}
            inputProps={fields[0].inputProps}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export function TextFieidGroupWithLabel(props: {
  value: { [key: string]: string | number };
  fields: any;
  onChange: Function;
  isEdit?: boolean;
  rootClass?: any;
  label: string;
}) {
  const { onChange, isEdit, label, value, fields } = props;
  const classes = useTextFiekdGroupWithLabelStyle({ noOfTextFields: fields.length });
  return (
    <>
      <Grid container alignItems="center" className={props.rootClass}>
        <Grid item className={classes.labelContainer}>
          <Typography className={classes.label}>{label}</Typography>
        </Grid>

        <Grid container style={{ width: '76%' }}>
          <Grid item container className={classes.inputsWrapper}>
            {fields.map((field: any) => (
              <Grid item className={classes.inputContainerOuterContainer} key={field.key}>
                <Typography className={classes.inputLabel}>{field.label}</Typography>

                <Grid
                  className={`${classes.inputContainer} ${
                    isEdit && !value[field.key] && field.highlightOnEdit ? classes.warning : ''
                  }`}
                >
                  <InputBase
                    className={classes.input}
                    placeholder={field.label}
                    type={field.type}
                    value={value[field.key]}
                    onChange={(e) => {
                      onChange({ ...value, [field.key]: e.target.value });
                    }}
                    inputProps={field.inputProps}
                  />
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

function Field(props: {
  classes: ReturnType<typeof useTextFiekdGroupWithLabelStyle>;
  valueKey: FormValueKeys;
  setter: Function;
  label?: string;
}) {
  const { classes, valueKey, setter, label } = props;
  const { error, warning } = useFieldValidator(valueKey);
  const value = useEditFormState((state) => state[valueKey] as string);
  const dispatch = useEditFormDispatch();

  return (
    <Grid item className={classes.inputContainerOuterContainer}>
      <Typography className={classes.inputLabel}>{label}</Typography>

      <Grid className={`${classes.inputContainer} ${warning ? classes.warning : ''}`}>
        <InputBase
          className={classes.input}
          placeholder={label}
          value={value || ''}
          onChange={(e) => {
            dispatch(setter(valueKey, e.target.value));
          }}
        />
      </Grid>
    </Grid>
  );
}

export function TextFieldGroup(props: {
  rootClass?: string;
  label: string;
  valueKeys: FormValueKeys[];
  setter: Function;
}) {
  const { label, valueKeys, setter } = props;
  const configs = useMultipleFieldConfig(valueKeys);
  const classes = useTextFiekdGroupWithLabelStyle({ noOfTextFields: configs.length });

  if (configs.length === 0) {
    return null;
  }

  return (
    <>
      <Grid container alignItems="center" className={props.rootClass}>
        <Grid item className={classes.labelContainer}>
          <Typography className={classes.label}>{label}</Typography>
        </Grid>

        <Grid container style={{ width: '76%' }}>
          <Grid item container className={classes.inputsWrapper}>
            {configs.map(({ valueKey, label: subLabel }) => (
              <Field
                key={valueKey}
                valueKey={valueKey}
                setter={setter}
                classes={classes}
                label={subLabel}
              />
            ))}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

export function Pages(props: { rootClass?: string; setter: Function }) {
  const { setter } = props;
  const classes = useTextFiekdGroupWithLabelStyle({ noOfTextFields: 2 });
  const { error, warning } = useFieldValidator('citedPages');
  const isEdit = useEditFormState((state) => state.isEdit);
  const { label, show } = useFieldConfig('citedPages');
  const { start, end } = useEditFormState((state) => state.citedPages) || {};
  const dispatch = useEditFormDispatch();

  if (!show) {
    return null;
  }

  return (
    <>
      <Grid container alignItems="center" className={props.rootClass}>
        <Grid item className={classes.labelContainer}>
          <Typography className={classes.label}>{label}</Typography>
        </Grid>

        <Grid container style={{ width: '76%' }}>
          <Grid item container className={classes.inputsWrapper}>
            <Grid item className={classes.inputContainerOuterContainer}>
              <Typography className={classes.inputLabel}>Start</Typography>

              <Grid
                className={`${classes.inputContainer} ${isEdit && warning ? classes.warning : ''}`}
              >
                <InputBase
                  className={classes.input}
                  placeholder="Start"
                  value={start || ''}
                  onChange={(e) => {
                    dispatch(setter('citedPages', { start: e.target.value, end }));
                  }}
                />
              </Grid>
            </Grid>

            <Grid item className={classes.inputContainerOuterContainer}>
              <Typography className={classes.inputLabel}>End</Typography>

              <Grid
                className={`${classes.inputContainer} ${isEdit && warning ? classes.warning : ''}`}
              >
                <InputBase
                  className={classes.input}
                  placeholder="End"
                  value={end || ''}
                  onChange={(e) => {
                    dispatch(setter('citedPages', { start, end: e.target.value }));
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}
